import { useState } from 'react';
import { playSound, Sound } from '~/assets/sounds';
import { Divider } from '~/components/ui/Divider';
import {
	NumberplatePreview,
	NumberPlatePreviewSize,
} from '~/components/ui/NumberplatePreview';
import { Text } from '~/components/ui/Text';
import { TextInput, TextInputAlign } from '~/components/ui/TextInput';
import { rage, useInputAction } from '~/rage';
import {
	IAdminRequestModalRow,
	IModalStateAdminRequest,
	InputAction,
} from '~/shared';
import { ModalButton } from '../ModalButton';
import {
	StyledButtonsContainer,
	StyledModalBody,
	StyledRow,
	StyledRowLabel,
	StyledRowsList,
	StyledRowValue,
} from './ModalAdminRequest.styled';

const REASON_MAX_LENGTH = 300;

const RequestRow: React.FC<{ row: IAdminRequestModalRow }> = ({ row }) => {
	if (row.kind === 'numberplate') {
		return (
			<StyledRow>
				<StyledRowLabel>{row.label}</StyledRowLabel>
				<StyledRowValue>
					{row.secondaryLabel && (
						<Text>
							<span>{row.secondaryLabel}</span>
						</Text>
					)}
					<NumberplatePreview
						numberPlate={row.numberPlate}
						size={NumberPlatePreviewSize.Size20}
					/>
				</StyledRowValue>
			</StyledRow>
		);
	}

	return (
		<StyledRow>
			<StyledRowLabel>{row.label}</StyledRowLabel>
			<StyledRowValue>
				<Text>
					{row.value} {row.valueSubtext && <span>{row.valueSubtext}</span>}
				</Text>
			</StyledRowValue>
		</StyledRow>
	);
};

const ModalAdminRequest: React.FC<{ state: IModalStateAdminRequest }> = ({
	state,
}) => {
	const [reason, setReason] = useState('');
	const [isReasonFocused, setIsReasonFocused] = useState(false);

	useInputAction(InputAction.CommonMenuAccept, () => {
		if (isReasonFocused) {
			return;
		}
		rage.modal.replyToCurrentModal({ isAccepted: true, reason });
	});

	useInputAction(InputAction.CommonMenuClose, () => {
		rage.modal.replyToCurrentModal({ isAccepted: false, reason });
	});

	const handleAccept = () => {
		rage.modal.replyToCurrentModal({ isAccepted: true, reason });
		playSound(Sound.ClickButton);
	};

	const handleCancel = () => {
		rage.modal.replyToCurrentModal({ isAccepted: false, reason });
		playSound(Sound.ClickButton);
	};

	return (
		<StyledModalBody>
			<StyledRowsList>
				{state.rows.map((row, index) => (
					<RequestRow key={index} row={row} />
				))}
				<Divider $direction="H" style={{ opacity: 0.1 }} />
				{state.reasonPlaceholder && (
					<TextInput
						height={50}
						fontSize={16}
						textAlign={TextInputAlign.Center}
						placeholder={state.reasonPlaceholder}
						value={reason}
						maxLength={REASON_MAX_LENGTH}
						onChange={(e) => setReason(e.target.value)}
						onFocus={() => setIsReasonFocused(true)}
						onBlur={() => setIsReasonFocused(false)}
						onKeyDown={(e) => {
							if (e.key === 'Enter') {
								e.preventDefault();
								handleAccept();
							}
						}}
					/>
				)}
			</StyledRowsList>

			<StyledButtonsContainer>
				<ModalButton
					$type={state.cancelButton.color}
					onClick={handleCancel}
					onMouseEnter={() => playSound(Sound.LinkCursor)}
				>
					{state.cancelButton.text}
				</ModalButton>
				<ModalButton
					$type={state.acceptButton.color}
					onClick={handleAccept}
					onMouseEnter={() => playSound(Sound.LinkCursor)}
				>
					{state.acceptButton.text}
				</ModalButton>
			</StyledButtonsContainer>
		</StyledModalBody>
	);
};

export { ModalAdminRequest };
